// src/utils/overlay.ts
import { Z } from "./constants";

/**
 * Central overlay map for Storycore.
 * Extend this if you want new overlay types (gradients, vignettes, etc).
 */
export const overlayClassMap: Record<string, string> = {
  dark: "bg-dark opacity-50",
  "soft-white": "overlay-soft-white",
  "soft-black": "overlay-soft-black",
  vignette: "overlay-vignette",
  "gradient-black-up": "overlay-gradient-black-up",
  "gradient-black-down": "overlay-gradient-black-down",
  "gradient-white-up": "overlay-gradient-white-up",
  "gradient-white-down": "overlay-gradient-white-down",
  "gradient-black-diagonal": "overlay-gradient-black-diagonal",
};

export function ensureOverlay(section: HTMLElement): HTMLElement {
  // Ensure section can anchor absolutely-positioned children
  const cs1 = getComputedStyle(section);
  if (cs1.position === "static") {
    section.style.position = "relative";
  }

  let container = section.querySelector<HTMLElement>(".sc-overlay");
  if (container) {
    return container;
  }
  container = document.createElement("div");
  container.className = "sc-overlay absolute inset-0 w-full h-full pointer-events-none";
  container.style.position = "absolute";
  container.style.zIndex = String(Z.OVERLAY);
  container.style.left = "0";
  container.style.top = "0";
  container.style.right = "0";
  container.style.bottom = "0";
  container.setAttribute("data-role", "overlay-container");
  section.appendChild(container);
  return container;
}

export function ensureImageOverlayContainer(section: HTMLElement, opts?: { interactive?: boolean }): HTMLElement {
  // Ensure section can anchor absolutely-positioned children
  const cs2 = getComputedStyle(section);
  if (cs2.position === "static") {
    section.style.position = "relative";
  }

  let container = section.querySelector<HTMLElement>(".sc-image-overlays");
  if (container) return container;
  container = document.createElement("div");
  const interactive = !!opts?.interactive;
  container.className = "sc-image-overlays absolute inset-0 w-full h-full" + (interactive ? "" : " pointer-events-none");
  container.style.position = "absolute";
  container.style.zIndex = String(Z.IMAGE_OVERLAYS); // below CONTENT (500)
  container.style.left = "0";
  container.style.top = "0";
  container.style.right = "0";
  container.style.bottom = "0";
  container.setAttribute("data-role", "image-overlay-container");
  section.appendChild(container);
  return container;
}

// Gradients/vignettes are non-interactive and always sit beneath image overlays (zIndex: 0 inside container).
export function applyOverlay(section: HTMLElement, overlayKey?: string) {
  if (!overlayKey) return;

  const overlayClass = overlayClassMap[overlayKey.toLowerCase()];
  if (!overlayClass) return;

  const overlay = document.createElement("div");
  overlay.className = `${overlayClass} absolute inset-0 w-full h-full`;
  overlay.style.zIndex = "0"; // gradient/vignette sits beneath image overlays inside the container

  const container = ensureOverlay(section);
  container.appendChild(overlay);
}

/** Map a variety of author inputs into canonical position classes.
 * Returns array of classes to apply on the ABSOLUTELY-POSITIONED WRAPPER.
 * Keep this close to overlay utils so both renderers can share the same logic.
 */
export function getImagePositionClasses(raw?: string): string[] {
  const t = String(raw || "top-left").trim().toLowerCase().replace(/\s+/g, "-");
  const map: Record<string, string> = {
    "top-left": "top-left", "tl": "top-left",
    "top-center": "top-center", "tc": "top-center",
    "top-right": "top-right", "tr": "top-right",
    "middle-left": "middle-left", "ml": "middle-left", "center-left": "middle-left",
    "center": "center", "middle": "center", "cc": "center",
    "middle-right": "middle-right", "mr": "middle-right", "center-right": "middle-right",
    "bottom-left": "bottom-left", "bl": "bottom-left",
    "bottom-center": "bottom-center", "bc": "bottom-center",
    "bottom-right": "bottom-right", "br": "bottom-right",
    // special house style (if defined in theme.css)
    "logo-top-right": "logo-top-right"
  };
  // Wrapper is positioned absolute by CSS via these classes; always include a base hook.
  return ["section-image", map[t] ?? "top-left"];
}

/** Create (and append) a positioned overlay host for an image or any node.
 * - Ensures the image overlay container exists.
 * - Returns the ABSOLUTE-POSITIONED WRAPPER element. Caller should append their IMG inside it.
 * - Wrapper is marked data-anim-skip="1" so animate.ts doesn't try to animate the container;
 *   attach data-anim attributes to the child IMG instead.
 */
export function createOverlayImageHost(
  section: HTMLElement,
  opts?: { position?: string; interactive?: boolean; extraClasses?: string | string[] }
): HTMLElement {
  const overlay = ensureImageOverlayContainer(section, { interactive: !!opts?.interactive });
  const classes = getImagePositionClasses(opts?.position);
  const host = document.createElement("div");
  host.classList.add(...classes);

  // Optional extra classes (e.g., responsive sizing utilities)
  const extra = opts?.extraClasses;
  if (extra) {
    const list = Array.isArray(extra) ? extra : String(extra).split(/\s+/);
    for (const c of list) if (c) host.classList.add(c);
  }

  // Prevent reveal driver from animating wrapper; child IMG should be the target.
  host.setAttribute("data-anim-skip", "1");

  overlay.appendChild(host);
  return host;
}

// ---- Overlay image helpers (unified spec parsing + attachment) ----

export type OverlayImageSpec =
  | string
  | {
      src?: string;
      alt?: string;
      // positioning (aliases supported)
      position?: string;
      corner?: string;
      pos?: string;
      top?: number | string;
      left?: number | string;
      right?: number | string;
      bottom?: number | string;
      z?: number | string;
      pad?: number | string;           // maps to Tailwind p-*
      pointer?: boolean | "auto" | "none";
      class?: string | string[];       // extra classes for the host
      // link support
      href?: string;
      target?: string;
      rel?: string;
      // animation
      anim?: string;
      delay?: number | string;         // supports "800ms"
      dur?: number | string;           // supports "3000ms"
      dist?: number | string;
    };

// Convert "3000ms" -> "3", keep "0.8" as "0.8"
function toSec(value?: number | string): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") return String(value);
  const v = String(value).trim().toLowerCase();
  if (!v) return undefined;
  if (v.endsWith("ms")) {
    const ms = parseFloat(v.slice(0, -2));
    return Number.isFinite(ms) ? String(ms / 1000) : undefined;
  }
  const n = parseFloat(v);
  return Number.isFinite(n) ? String(n) : undefined;
}

function pxOrNum(v?: number | string): string | undefined {
  if (v == null) return undefined;
  if (typeof v === "number") return `${v}px`;
  const s = String(v).trim();
  return /[a-z%]$/i.test(s) ? s : `${s}px`;
}

function padClass(pad?: number | string): string {
  if (pad == null || pad === "") return "p-3"; // default matches existing behavior
  const n = typeof pad === "number" ? pad : parseInt(String(pad), 10);
  const clamped = Number.isFinite(n) ? Math.max(0, Math.min(8, n)) : 0;
  return `p-${clamped}`;
}

/** Parse strings like `file.png { position: top-left, delay: 800ms, dur: 3 }`
 *  or pass through objects unchanged.
 */
function parseOverlayImageSpec(input: OverlayImageSpec): Record<string, any> {
  if (typeof input !== "string") return { ...(input as any) };

  // minimal tolerant parser
  const m = input.match(/^\s*([^{}\s]+)\s*(\{([\s\S]*)\})?\s*$/);
  const spec: Record<string, any> = {};
  if (!m) {
    // treat the whole string as src
    spec.src = input.trim();
    return spec;
  }
  spec.src = m[1].trim();
  const body = (m[3] || "").trim();
  if (!body) return spec;

  // split on commas not inside quotes (simple)
  const parts = body.split(",").map((p) => p.trim()).filter(Boolean);
  for (const part of parts) {
    const kv = part.split(":");
    const key = (kv[0] || "").trim();
    let raw = (kv[1] || "").trim();
    if (!key) continue;
    // strip wrapping quotes
    raw = raw.replace(/^"(.*)"$/, "$1").replace(/^'(.*)'$/, "$1");
    // coerce number when safe
    const maybe = Number(raw);
    spec[key] = Number.isNaN(maybe) || /[^\d.]/.test(raw) ? raw : maybe;
  }
  return spec;
}

/** Attach an overlay image (or link-wrapped image) to the section’s image overlay container.
 * - Positions via `position` (or aliases: corner/pos) using getImagePositionClasses().
 * - Supports ms → s conversion for delay/dur.
 * - Puts data-anim* on the IMG only; wrapper gets data-anim-skip="1".
 * - Never sets data-animated or calls GSAP here.
 */
export function attachOverlayImage(
  section: HTMLElement,
  specInput: OverlayImageSpec
): HTMLElement {
  const s = parseOverlayImageSpec(specInput);

  // Resolve position token from any alias
  const positionToken: string | undefined = s.position ?? s.corner ?? s.pos;

  // If link present, make the container interactive so links are clickable.
  const interactive = !!s.href || s.pointer === "auto" || s.pointer === true;

  const host = createOverlayImageHost(section, {
    position: positionToken,
    interactive,
    extraClasses: [padClass(s.pad)]
      .concat(Array.isArray(s.class) ? s.class : (s.class ? String(s.class).split(/\s+/) : []))
      .filter(Boolean)
  });

  // Optional explicit offsets override position classes
  const hasExplicitOffsets = s.top != null || s.left != null || s.right != null || s.bottom != null;
  if (hasExplicitOffsets) {
    if (s.top != null) host.style.top = pxOrNum(s.top)!;
    if (s.left != null) host.style.left = pxOrNum(s.left)!;
    if (s.right != null) host.style.right = pxOrNum(s.right)!;
    if (s.bottom != null) host.style.bottom = pxOrNum(s.bottom)!;
  }

  // z-index override for the host (rare but supported)
  if (s.z != null) host.style.zIndex = String(s.z);

  // Build the content: either <a><img/></a> or <img/>
  let imgEl: HTMLImageElement;
  if (s.href) {
    const a = document.createElement("a");
    a.href = String(s.href);
    a.target = s.target || "_blank";
    a.rel = s.rel || "noopener noreferrer";
    // ensure pointer events are enabled on interactive overlays
    (host as HTMLElement).style.pointerEvents = "auto";
    a.style.pointerEvents = "auto";

    imgEl = document.createElement("img");
    imgEl.decoding = "async";
    imgEl.loading = "lazy";
    imgEl.src = String(s.src || "");
    imgEl.alt = s.alt || (String(s.src || "").split("/").pop()?.split(".")[0] ?? "overlay");
    a.appendChild(imgEl);
    host.appendChild(a);
  } else {
    imgEl = document.createElement("img");
    imgEl.decoding = "async";
    imgEl.loading = "lazy";
    imgEl.src = String(s.src || "");
    imgEl.alt = s.alt || (String(s.src || "").split("/").pop()?.split(".")[0] ?? "overlay");
    host.appendChild(imgEl);
  }

  // Animation attributes on the IMG (not the wrapper)
  const animToken = (s.anim || "fade-in").toString().trim();
  if (animToken) imgEl.setAttribute("data-anim", animToken);

  const delaySec = toSec(s.delay);
  if (delaySec) imgEl.setAttribute("data-anim-delay", delaySec);

  const durSec = toSec(s.dur);
  if (durSec) imgEl.setAttribute("data-anim-dur", durSec);

  if (s.dist != null && String(s.dist).trim() !== "") {
    imgEl.setAttribute("data-anim-dist", String(s.dist));
  }

  // The wrapper host already has data-anim-skip="1" from createOverlayImageHost()
  // We intentionally do not set data-animated here; animateElements() will handle it.

  return host;
}